import { normalizePhone } from './phone.js';
import { z } from 'zod';
import { EMPLOYMENT_TYPES, SHIFTS } from './constants.js';
import { titleCaseName } from './format.js';

/** Validation schemas shared by API and web. Keep request/response shapes here. */

export const latLngSchema = z.object({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  accuracy_m: z.number().min(0).max(100_000),
});
export type LatLngInput = z.infer<typeof latLngSchema>;

export const phoneSchema = z
  .string()
  .trim()
  .regex(/^\+?[0-9]{10,15}$/, 'Phone must be 10–15 digits, optional leading +');

/** Indian mobile stored as +91 followed by 10 digits; the form shows the prefix fixed. */
export const indianPhoneSchema = z
  .string()
  .trim()
  .transform((v, ctx) => {
    const n = normalizePhone(v);
    if (!n) {
      ctx.addIssue({ code: 'custom', message: 'Phone must be +91 followed by 10 digits' });
      return z.NEVER;
    }
    return n;
  });
/** Only for reading a stored code back; nobody may submit one. */
export const employeeCodeSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9-]{2,20}$/, 'Employee code: letters, digits and dashes (2–20)');
const ymdSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
export const ENGAGEMENT_TYPES = ['contractor', 'employee'] as const;
export type EngagementType = (typeof ENGAGEMENT_TYPES)[number];

export const createWorkerSchema = z.object({
  // Saved in printed case whatever was typed — see titleCaseName. In the schema rather than at
  // each route so the admin's form, the edit, and any import all get it.
  first_name: z.string().trim().min(1, 'Enter the first name').max(60).transform(titleCaseName),
  /**
   * Optional: several people on this roster go by one name, and requiring a surname meant
   * inventing one or typing a full stop. Empty becomes null rather than an empty string, so the
   * two cases cannot drift apart in the database.
   *
   * `undefined` survives on purpose. updateWorkerSchema is this schema made partial, and the edit
   * route reads `body.last_name ?? before.lastName` — if a missing field became null here, every
   * edit of a phone number would quietly erase the surname.
   */
  last_name: z
    .string()
    .trim()
    .max(60)
    .transform(titleCaseName)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),
  phone: indianPhoneSchema,
  employment_type: z.enum(EMPLOYMENT_TYPES),
  /**
   * Optional so that everything already sending this form keeps working: the column defaults to
   * the first shift, which is where this workforce was until the second one existed.
   */
  shift: z.enum(SHIFTS).optional(),
  join_date: ymdSchema,
  /** Contractor unless said otherwise. Employees are not implemented yet. */
  engagement_type: z.enum(ENGAGEMENT_TYPES).optional(),
  designation_id: z.uuid().nullable().optional(),
  /** TDS under 194C, in basis points. 100 is one percent, which is the usual case. */
  tds_rate_bp: z.number().int().min(0).max(10_000).optional(),
});
export type CreateWorkerInput = z.infer<typeof createWorkerSchema>;

export const timeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM');

/** One shift's window. An end before its start means it finishes after midnight, which is legal. */
export const shiftWindowSchema = z.object({ start: timeOfDaySchema, end: timeOfDaySchema });

export const officeSettingsSchema = z.object({
  office_name: z.string().trim().min(1).max(100),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  radius_m: z.number().int().min(20).max(5000),
  // Optional since 16 October 2026: no longer on the Settings page; absent leaves it unchanged.
  office_public_ip: z.string().trim().max(45).nullable().optional(),
  /**
   * The Google Group join link for the Play testing track. Optional, and validated as an https
   * URL when given: a typo here sends every new employee to a dead page, and the one person who
   * would notice is the one who cannot fix it.
   */
  /**
   * How an employee is told to install the app. Changing it is how the owner moves between the
   * direct download, the Play testing track and a public listing, without a deploy.
   */
  app_install_route: z.enum(['download', 'play_group', 'play_public']).optional(),
  play_group_url: z
    .string()
    .trim()
    .max(300)
    .nullable()
    .optional()
    .transform((v) => (v ? v : null))
    .refine((v) => v === null || /^https:\/\/\S+$/.test(v), 'Must be an https:// link'),
  day_cutoff_time: timeOfDaySchema,
  digest_time: timeOfDaySchema,
  daily_report_time: timeOfDaySchema,
  monthly_report_enabled: z.boolean(),
  punchout_reminder_time: timeOfDaySchema,
  /** The four shift windows. Shaped loosely here and normalised by shiftTimes() on the way out. */
  shift_times: z
    .object({
      full_time: z.object({ first: shiftWindowSchema, second: shiftWindowSchema }),
      part_time: z.object({ first: shiftWindowSchema, second: shiftWindowSchema }),
    })
    .optional(),
  off_cap_total: z.number().int().min(0).max(30),
  off_cap_full_time: z.number().int().min(0).max(30),
  off_cap_part_time: z.number().int().min(0).max(30),
  // Office hours were removed on 23 September: this workforce has no start time. A full-time
  // employee owes nine hours and a part-time one five, whenever they are worked.
  /**
   * Whether app binding is enforced. Binding is recorded either way, so the rollout can watch
   * for a week before anything starts being refused.
   */
  android_app_required: z.boolean().optional(),
  /**
   * Require the app of everybody, not only of those already known to have it. Turning this on
   * stops anybody who has not installed yet from punching, so it is a deliberate act.
   */
  app_required_for_all: z.boolean().optional(),
  /** Builds below this are refused at the API. 0 means no floor. */
  android_min_version_code: z.number().int().min(0).max(1_000_000).optional(),
  /** Everybody on the newest build anybody is running, rather than on a number typed by hand. */
  android_require_newest: z.boolean().optional(),
});
export type OfficeSettingsInput = z.infer<typeof officeSettingsSchema>;

// ---------- auth ----------
export const adminLoginSchema = z.object({
  email: z.email().max(200),
  password: z.string().min(1).max(200),
  /** Keep me signed in on this device (30-day session bound to a device cookie). */
  remember: z.boolean().optional(),
});
export const forgotPasswordSchema = z.object({ email: z.email().max(200) });
export const resetPasswordSchema = z.object({
  password: z.string().min(12, 'At least 12 characters').max(200),
});
export type AdminLoginInput = z.infer<typeof adminLoginSchema>;

export const createAdminSchema = z.object({
  name: z.string().trim().min(2).max(100),
  email: z.email().max(200),
  password: z.string().min(12, 'At least 12 characters').max(200),
});
export type CreateAdminInput = z.infer<typeof createAdminSchema>;

export const updateWorkerSchema = createWorkerSchema.partial();
/** The employee code is allocated by the server and never changes. */
export const READ_ONLY_WORKER_FIELDS = ['employee_code'] as const;
export type UpdateWorkerInput = z.infer<typeof updateWorkerSchema>;

export const REREGISTER_REASONS = ['new_phone', 'lost_phone', 'other'] as const;
export type ReregisterReason = (typeof REREGISTER_REASONS)[number];

export const reregisterRequestSchema = z.object({
  /** Required when not signed in; ignored when a worker session is present. */
  phone: phoneSchema.optional(),
  reason: z.enum(REREGISTER_REASONS),
  note: z.string().trim().max(300).optional(),
});
export type ReregisterRequestInput = z.infer<typeof reregisterRequestSchema>;

/** Shape check only; @simplewebauthn/server does the real validation. */
export const webauthnResponseSchema = z.looseObject({
  id: z.string().min(1),
  rawId: z.string().min(1),
  type: z.string(),
  response: z.record(z.string(), z.unknown()),
});

export const ceremonyVerifySchema = z.object({
  challengeId: z.uuid(),
  response: webauthnResponseSchema,
});
export type CeremonyVerifyInput = z.infer<typeof ceremonyVerifySchema>;

export const enrolPasswordSchema = z.object({ password: z.string().min(1).max(200) });
export const enrolVerifySchema = ceremonyVerifySchema.extend({
  password: z.string().min(1).max(200),
});

export const decisionNoteSchema = z.object({ note: z.string().trim().max(300).optional() });

// ---------- weekly off swaps ----------
export const offSwapRequestSchema = z.object({
  /** The worker's own off day that week, and the day they would rather have. */
  from_date: ymdSchema,
  to_date: ymdSchema,
  /** A colleague already off on `to_date`, asked first. Omitted means "admin, please move me". */
  counterparty_id: z.uuid().nullable().optional(),
  reason: z.string().trim().max(300).optional(),
});
export type OffSwapRequestInput = z.infer<typeof offSwapRequestSchema>;

export const offSwapRespondSchema = z.object({ accept: z.boolean() });

// ---------- punch ----------
export const punchLocationSchema = latLngSchema; // { lat, lng, accuracy_m }

export const EARLY_OUT_REASON_CODES = [
  'personal_emergency',
  'unwell',
  'work_completed',
  'other',
] as const;

export const punchSchema = ceremonyVerifySchema.extend({
  /** Optional: what the client thinks it is doing; must match the server's open-shift view. */
  type: z.enum(['in', 'out']).optional(),
  /** null when the browser could not provide a position (rejected with an instruction). */
  location: punchLocationSchema.nullable(),
  /** Required only when punching out below the day's target; never blocks the punch itself. */
  early_out_reason_code: z.enum(EARLY_OUT_REASON_CODES).optional(),
  early_out_reason: z.string().trim().min(10).max(200).optional(),
  /**
   * From the native Android app only. The integrity token is what Android signed about the
   * device and the app; the server hands it to Google to decode, because the decoded payload is
   * the trustworthy part. A browser sends none of this and is treated accordingly.
   */
  integrity_token: z.string().min(1).max(20_000).optional(),
  /**
   * Android's own answer to "was this position from a mock provider". A browser cannot be asked
   * and never sends it; the app sending false is not proof of anything on its own, which is why
   * the integrity verdict has to stand behind it.
   */
  mock_location: z.boolean().optional(),
  app_version_code: z.number().int().min(1).max(1_000_000).optional(),
  app_version_name: z.string().max(40).optional(),
});
export type PunchInput = z.infer<typeof punchSchema>;

/** Outside-geofence report from the app: location only, no ceremony. */
export const punchReportSchema = z.object({ location: punchLocationSchema });
